/**
 * Organisation Aggregate Root
 * 
 * Enterprise-grade DDD implementation
 * NO duplicate entity files!
 */
import { AggregateRootBase } from '../../../../shared/domain/AggregateRootBase';
import { v4 as uuidv4 } from 'uuid';

// Value Objects
export class OrganisationName {
  constructor(
    public readonly legalName: string,
    public readonly tradingName?: string
  ) {
    if (!legalName || legalName.trim().length === 0) {
      throw new Error('Legal name is required');
    }
  }

  getDisplayName(): string {
    return this.tradingName || this.legalName;
  }

  equals(other: OrganisationName): boolean {
    return this.legalName === other.legalName &&
           this.tradingName === other.tradingName;
  }
}

export class RegistrationNumber {
  constructor(public readonly value: string) {
    if (!value || value.trim().length === 0) {
      throw new Error('Registration number is required');
    }
  }

  equals(other: RegistrationNumber): boolean {
    return this.value === other.value;
  }
}

// Domain Events
export interface OrganisationCreated {
  aggregateId: string;
  legalName: string;
  industry?: string;
  occurredAt: Date;
}

export interface OrganisationUpdated {
  aggregateId: string;
  changedFields: string[];
  occurredAt: Date;
}

export interface OrganisationDeactivated {
  aggregateId: string;
  reason: string;
  occurredAt: Date;
}

/**
 * Organisation Aggregate Root
 */
export class Organisation extends AggregateRootBase {
  private constructor(
    id: string,
    private name: OrganisationName,
    private registrationNumber?: RegistrationNumber,
    private industry?: string,
    private website?: string,
    private isActive: boolean = true,
    private parentOrganisationId?: string,
    private createdAt?: Date,
    private updatedAt?: Date,
    version: number = 0
  ) {
    super(id, version);
  }

  /**
   * Factory method to create new Organisation
   */
  static create(
    legalName: string,
    tradingName?: string,
    registrationNumber?: string,
    industry?: string
  ): Organisation {
    const id = uuidv4();
    const name = new OrganisationName(legalName, tradingName);
    const regNumber = registrationNumber ? new RegistrationNumber(registrationNumber) : undefined;
    const now = new Date();

    const org = new Organisation(
      id,
      name,
      regNumber,
      industry,
      undefined, // website
      true, // isActive
      undefined, // parentOrganisationId
      now,
      now
    );

    org.addDomainEvent<OrganisationCreated>({
      aggregateId: id,
      legalName,
      industry,
      occurredAt: now
    });

    return org;
  }

  /**
   * Reconstitute from persistence
   */
  static fromSnapshot(snapshot: any): Organisation {
    return new Organisation(
      snapshot.id,
      new OrganisationName(snapshot.legalName, snapshot.tradingName),
      snapshot.registrationNumber ? new RegistrationNumber(snapshot.registrationNumber) : undefined,
      snapshot.industry,
      snapshot.website,
      snapshot.isActive,
      snapshot.parentOrganisationId,
      snapshot.createdAt,
      snapshot.updatedAt,
      snapshot.version
    );
  }

  /**
   * Update organisation details
   */
  updateDetails(
    tradingName?: string,
    industry?: string,
    website?: string
  ): void {
    const changedFields: string[] = [];

    if (tradingName !== undefined && tradingName !== this.name.tradingName) {
      this.name = new OrganisationName(this.name.legalName, tradingName);
      changedFields.push('tradingName');
    }

    if (industry !== undefined && industry !== this.industry) {
      this.industry = industry;
      changedFields.push('industry');
    }

    if (website !== undefined && website !== this.website) {
      this.website = website;
      changedFields.push('website');
    }

    if (changedFields.length > 0) {
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<OrganisationUpdated>({
        aggregateId: this.id,
        changedFields,
        occurredAt: new Date()
      });
    }
  }

  /**
   * Set parent organisation
   */
  setParentOrganisation(parentId: string): void {
    if (parentId === this.id) {
      throw new Error('Organisation cannot be its own parent');
    }

    if (this.parentOrganisationId !== parentId) {
      this.parentOrganisationId = parentId;
      this.updatedAt = new Date();
      this.incrementVersion();

      this.addDomainEvent<OrganisationUpdated>({
        aggregateId: this.id,
        changedFields: ['parentOrganisationId'],
        occurredAt: new Date()
      });
    }
  }

  /**
   * Deactivate organisation
   */
  deactivate(reason: string): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.updatedAt = new Date();
    this.incrementVersion();

    this.addDomainEvent<OrganisationDeactivated>({
      aggregateId: this.id,
      reason,
      occurredAt: new Date()
    });
  }

  /**
   * Reactivate organisation
   */
  reactivate(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.updatedAt = new Date();
    this.incrementVersion();
  }

  // Getters
  getLegalName(): string { return this.name.legalName; }
  getTradingName(): string | undefined { return this.name.tradingName; }
  getDisplayName(): string { return this.name.getDisplayName(); }
  getRegistrationNumber(): string | undefined { return this.registrationNumber?.value; }
  getIndustry(): string | undefined { return this.industry; }
  getWebsite(): string | undefined { return this.website; }
  isOrganisationActive(): boolean { return this.isActive; }
  getParentOrganisationId(): string | undefined { return this.parentOrganisationId; }

  // Snapshot for persistence
  toSnapshot(): any {
    return {
      id: this.id,
      legalName: this.name.legalName,
      tradingName: this.name.tradingName,
      registrationNumber: this.registrationNumber?.value,
      industry: this.industry,
      website: this.website,
      isActive: this.isActive,
      parentOrganisationId: this.parentOrganisationId,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      version: this.version
    };
  }
}